<script setup lang="ts">
import { computed } from 'vue'
import type { Tone } from '../tokens'
import { toneVars } from '../tone-vars'

/**
 * 报错条。
 *
 * 输入是项目统一的那句话:`[CODE] 消息（requestId xxx）`(client.ts 拼的)。
 * 它在正文里当一整行读是很糟的 —— 出错时人要一眼看到三件事:
 * **是什么错**(错误码)、**说了什么**(消息)、**报障要抄哪个号**(requestId)。
 * 所以这里把它拆开排版,但一个字都不丢。
 *
 * 拆不出来就整串原样显示:**不允许**因为「格式不认识」而少显示一截 ——
 * 那种失效发生在最需要看清错误的那一次。
 *
 * requestId 用等宽字体并放右下角,是因为它的用途就是被选中复制粘贴。
 */
const props = withDefaults(defineProps<{ message: string | null; title?: string; tone?: Tone }>(), {
  title: '请求失败',
  tone: 'light',
})

interface Parsed {
  readonly code: string | null
  readonly message: string
  readonly requestId: string | null
}

/** `[CODE] 正文（requestId xxx）`。正文里本来就可能有括号,所以锚定在末尾那一组上 */
const SHAPE = /^\[([^\]]+)\]\s*([\s\S]*?)\s*[（(]\s*requestId\s+([^）)]*?)\s*[）)]$/

const parsed = computed<Parsed | null>(() => {
  const raw = props.message
  if (!raw) return null

  const matched = SHAPE.exec(raw)
  if (!matched) return { code: null, message: raw, requestId: null }

  return {
    code: matched[1] ?? null,
    message: (matched[2] ?? '').trim(),
    requestId: (matched[3] ?? '').trim() || null,
  }
})

const vars = computed(() => toneVars(props.tone))
</script>

<template>
  <div v-if="parsed" class="banner" :style="vars" role="alert">
    <div class="head">
      <span class="title">{{ props.title }}</span>
      <span v-if="parsed.code" class="code">{{ parsed.code }}</span>
    </div>
    <p class="message">{{ parsed.message }}</p>
    <div v-if="parsed.requestId" class="request-id">
      <span class="request-id-label">requestId</span>
      <span class="request-id-value">{{ parsed.requestId }}</span>
    </div>
  </div>
</template>

<style scoped>
.banner {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border-radius: var(--sd-radius-md);
  background: var(--sd-danger-bg);
  border: 1px solid var(--sd-danger-border);
}

.head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title {
  font-size: 13px;
  font-weight: 600;
  color: var(--sd-danger);
}

.code {
  font-family: var(--sd-font-mono);
  font-size: 11px;
  padding: 0 8px;
  line-height: 18px;
  border-radius: var(--sd-radius-pill);
  color: var(--sd-danger);
  background: var(--sd-danger-bg);
  border: 1px solid var(--sd-danger-border);
}

.message {
  margin: 0;
  font-size: 13px;
  line-height: 1.7;
  color: var(--sd-text1);
  word-break: break-word;
}

.request-id {
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-end;
}

.request-id-label {
  font-size: 11px;
  color: var(--sd-text3);
}

.request-id-value {
  font-family: var(--sd-font-mono);
  font-size: 11px;
  color: var(--sd-text2);
  user-select: all;
}
</style>
